import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import type { PDFPageView as PDFPageViewType } from 'pdfjs-dist/web/pdf_viewer.mjs';
import DsButton from '../DsButton';
import { loadPdfDocument, PDFJS_RESOURCE_PATHS } from '../../../utils/pdfDocumentLoader';
import { Sentry } from '../../../setupSentry';
import 'pdfjs-dist/web/pdf_viewer.css';

type WebPdfDocumentPreviewProps = {
  blob: Blob;
  fileName: string;
  onError?: (_message: string) => void;
  toolbarActions?: React.ReactNode;
};

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = 0.25;
const FIT_GUTTER = 32;
const PDF_TO_CSS_UNITS = 96 / 72;

const clampZoom = (value: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

export default function WebPdfDocumentPreview({
  blob,
  fileName,
  onError,
  toolbarActions,
}: WebPdfDocumentPreviewProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const pagesRef = useRef<HTMLDivElement | null>(null);
  const pageViewsRef = useRef<PDFPageViewType[]>([]);
  const renderSequenceRef = useRef(0);
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(1);
  const [rendering, setRendering] = useState(true);

  useEffect(() => {
    let active = true;
    let loadedDocument: PDFDocumentProxy | null = null;
    setPdfDocument(null);
    setPageCount(0);
    setZoom(1);
    setRendering(true);

    blob.arrayBuffer()
      .then(loadPdfDocument)
      .then((document) => {
        loadedDocument = document;
        if (!active) {
          document.destroy();
          return;
        }
        setPdfDocument(document);
        setPageCount(document.numPages);
      })
      .catch((error: unknown) => {
        if (!active) return;
        Sentry.captureException(error, { tags: { component: 'WebPdfDocumentPreview', stage: 'load' } });
        setRendering(false);
        onError?.(error instanceof Error ? error.message : 'PDFを表示できませんでした。');
      });

    return () => {
      active = false;
      renderSequenceRef.current += 1;
      pageViewsRef.current.forEach((pageView) => pageView.destroy());
      pageViewsRef.current = [];
      loadedDocument?.destroy();
    };
  }, [blob, onError]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    const updateSize = () => {
      const nextSize = {
        width: Math.round(viewport.clientWidth),
        height: Math.round(viewport.clientHeight),
      };
      setContainerSize((currentSize) => (
        currentSize.width === nextSize.width && currentSize.height === nextSize.height
          ? currentSize
          : nextSize
      ));
    };
    updateSize();
    if (typeof ResizeObserver !== 'function') return undefined;
    const observer = new ResizeObserver(updateSize);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, []);

  const renderPages = useCallback(async () => {
    const pagesElement = pagesRef.current;
    if (!pdfDocument || !pagesElement || containerSize.width <= 0 || containerSize.height <= 0) return;
    const sequence = renderSequenceRef.current + 1;
    renderSequenceRef.current = sequence;
    setRendering(true);
    pageViewsRef.current.forEach((pageView) => pageView.destroy());
    pageViewsRef.current = [];
    pagesElement.replaceChildren();

    try {
      const {
        EventBus,
        LinkTarget,
        PDFPageView,
        SimpleLinkService,
      } = await import('pdfjs-dist/web/pdf_viewer.mjs');
      const eventBus = new EventBus();
      const linkService = new SimpleLinkService({
        eventBus,
        externalLinkTarget: LinkTarget.BLANK,
        externalLinkRel: 'noopener noreferrer nofollow',
      });
      linkService.setDocument(pdfDocument);
      const availableWidth = Math.max(containerSize.width - FIT_GUTTER, 1);
      const availableHeight = Math.max(containerSize.height - FIT_GUTTER, 1);
      for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
        if (renderSequenceRef.current !== sequence) return;
        const page = await pdfDocument.getPage(pageNumber);
        const naturalViewport = page.getViewport({ scale: 1 });
        const fitScale = Math.min(
          availableWidth / naturalViewport.width,
          availableHeight / naturalViewport.height,
        );
        const viewScale = (fitScale * zoom) / PDF_TO_CSS_UNITS;
        const pageView = new PDFPageView({
          container: pagesElement,
          eventBus,
          id: pageNumber,
          scale: viewScale,
          defaultViewport: naturalViewport,
          imageResourcesPath: PDFJS_RESOURCE_PATHS.imageResourcesPath,
          layerProperties: {
            annotationEditorUIManager: null,
            annotationStorage: pdfDocument.annotationStorage,
            downloadManager: null,
            enableScripting: false,
            fieldObjectsPromise: pdfDocument.getFieldObjects(),
            findController: null,
            hasJSActionsPromise: pdfDocument.hasJSActions(),
            linkService,
          },
        });
        pageView.div.classList.add('pdf-document-preview__page');
        pageView.div.setAttribute('aria-label', `${pageNumber}ページ目`);
        pageView.setPdfPage(page);
        pageViewsRef.current.push(pageView);
        await pageView.draw();
        pageView.div.querySelector('canvas')?.setAttribute(
          'aria-label',
          `${fileName} ${pageNumber}ページ目`,
        );
      }
    } catch (error: unknown) {
      if (renderSequenceRef.current === sequence) {
        Sentry.captureException(error, { tags: { component: 'WebPdfDocumentPreview', stage: 'render' } });
        onError?.(error instanceof Error ? error.message : 'PDFを表示できませんでした。');
      }
    } finally {
      if (renderSequenceRef.current === sequence) setRendering(false);
    }
  }, [containerSize.height, containerSize.width, fileName, onError, pdfDocument, zoom]);

  useEffect(() => {
    renderPages();
  }, [renderPages]);

  return (
    <div className="pdf-document-preview">
      <div className="pdf-document-preview__toolbar" role="toolbar" aria-label="PDF表示操作">
        <div className="pdf-document-preview__identity">
          <span className="pdf-document-preview__filename" title={fileName}>{fileName}</span>
          <span className="pdf-document-preview__pages" aria-label={`全${pageCount}ページ`}>
            {pageCount ? `1 / ${pageCount}` : '- / -'}
          </span>
        </div>
        <div className="pdf-document-preview__controls">
          <div className="pdf-document-preview__zoom" aria-label="表示倍率">
            <DsButton
              variant="secondary"
              size="sm"
              icon
              aria-label="縮小"
              title="縮小"
              disabled={zoom <= MIN_ZOOM}
              onClick={() => setZoom((value) => clampZoom(value - ZOOM_STEP))}
            >
              <i className="fa-solid fa-minus" aria-hidden="true" />
            </DsButton>
            <span>{Math.round(zoom * 100)}%</span>
            <DsButton
              variant="secondary"
              size="sm"
              icon
              aria-label="拡大"
              title="拡大"
              disabled={zoom >= MAX_ZOOM}
              onClick={() => setZoom((value) => clampZoom(value + ZOOM_STEP))}
            >
              <i className="fa-solid fa-plus" aria-hidden="true" />
            </DsButton>
          </div>
          {toolbarActions ? (
            <div className="pdf-document-preview__actions">{toolbarActions}</div>
          ) : null}
        </div>
      </div>
      <div ref={viewportRef} className="pdf-document-preview__viewport">
        {rendering ? <div className="pdf-document-preview__loading" role="status">PDFを描画しています...</div> : null}
        <div ref={pagesRef} className="pdf-document-preview__pages-list pdfViewer" />
      </div>
      <p className="pdf-document-preview__original-notice">
        表示内容に不足があるように見える場合は、［保存］して端末のPDFアプリで原本をご確認ください。
      </p>
    </div>
  );
}
